import { DialogComponent, DialogOpenEvent } from '@theme/dialog';

class SizeGuideModal extends DialogComponent {
  requiredRefs = ['dialog', 'content'];

  isLoaded = false;
  loadPromise = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogOpenEvent.eventName, this.loadContent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogOpenEvent.eventName, this.loadContent);
  }

  /**
   * Opens the modal from a normal link while preserving the link as a no-JS fallback.
   * @param {Event} event
   */
  openFromLink = (event) => {
    event.preventDefault();
    this.showDialog();
  };

  loadContent = async () => {
    if (this.isLoaded || this.loadPromise) return;

    const content = this.refs.content;
    if (!(content instanceof HTMLElement)) return;

    const sizeGuideUrl = this.getSizeGuideUrl();

    this.dataset.loading = 'true';
    content.setAttribute('aria-busy', 'true');

    this.loadPromise = this.getRenderedSizeGuide(sizeGuideUrl)
      .then((page) => {
        content.replaceChildren(page);
        this.isLoaded = true;
      })
      .catch(() => {
        content.innerHTML = `
          <div class="size-guide-modal__error" role="alert">
            <p>We could not load the size guide inside the popup.</p>
            <a href="${sizeGuideUrl}">Open the size guide page</a>
          </div>
        `;
      })
      .finally(() => {
        content.removeAttribute('aria-busy');
        delete this.dataset.loading;
        this.loadPromise = null;
        this.dataset.loaded = 'true';
      });
  };

  getSizeGuideUrl() {
    const url = new URL(this.dataset.sizeGuideUrl || '/pages/size-guide', window.location.origin);
    const currentUrl = new URL(window.location.href);
    const previewParams = ['preview_theme_id', '_fd', 'pb', 'locale', 'country'];

    previewParams.forEach((param) => {
      if (!url.searchParams.has(param) && currentUrl.searchParams.has(param)) {
        url.searchParams.set(param, currentUrl.searchParams.get(param));
      }
    });

    return url;
  }

  /**
   * Fetches the page once to discover the real JSON-template section IDs, then
   * asks Shopify to render those sections with their component styles included.
   * @param {URL} sizeGuideUrl
   * @returns {Promise<HTMLElement>}
   */
  async getRenderedSizeGuide(sizeGuideUrl) {
    const html = await this.fetchText(sizeGuideUrl);
    const pageDocument = new DOMParser().parseFromString(html, 'text/html');
    const mainContent = pageDocument.querySelector('#MainContent');
    const sectionIds = Array.from(mainContent?.querySelectorAll(':scope > .shopify-section[id]') || [])
      .map((section) => section.id.replace(/^shopify-section-/, ''))
      .filter(Boolean);

    const page = document.createElement('div');
    page.className = 'size-guide-modal__page';

    if (sectionIds.length === 0) {
      page.innerHTML = mainContent?.innerHTML || pageDocument.body.innerHTML;
      return page;
    }

    const renderedSections = await this.fetchSections(sizeGuideUrl, sectionIds);

    sectionIds.forEach((sectionId) => {
      if (renderedSections[sectionId]) {
        page.insertAdjacentHTML('beforeend', renderedSections[sectionId]);
      }
    });

    if (!page.hasChildNodes()) {
      page.innerHTML = mainContent?.innerHTML || pageDocument.body.innerHTML;
    }

    return page;
  }

  /**
   * @param {URL} url
   * @returns {Promise<string>}
   */
  async fetchText(url) {
    const response = await fetch(url, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error(`Size guide request failed with ${response.status}`);
    }

    return response.text();
  }

  /**
   * @param {URL} sizeGuideUrl
   * @param {string[]} sectionIds
   * @returns {Promise<Record<string, string>>}
   */
  async fetchSections(sizeGuideUrl, sectionIds) {
    const renderedSections = {};
    const batchSize = 5;

    for (let index = 0; index < sectionIds.length; index += batchSize) {
      const batch = sectionIds.slice(index, index + batchSize);
      const url = new URL(sizeGuideUrl);

      url.searchParams.set('sections', batch.join(','));

      const response = await fetch(url, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`Size guide sections request failed with ${response.status}`);
      }

      Object.assign(renderedSections, await response.json());
    }

    return renderedSections;
  }
}

if (!customElements.get('size-guide-modal')) {
  customElements.define('size-guide-modal', SizeGuideModal);
}
